import { getPublicSupabaseStatus, publicSupabase } from '@/lib/publicSupabase'
import {
  buildTrendEntityKey,
  createTrendEntitySlugMap,
  normalizeLeague,
  normalizeMarket,
  normalizeTeamName
} from '@/lib/quickStats/teamSlug'

const timeframePriority = ['current season', 'full-season', 'season', '3m', '1m', '7d', '1d']
const categoryDisplayMap = {
  underdogs_all: 'Underdogs',
  favorites_all: 'Favorites',
  underdogs_home: 'Home Underdogs',
  underdogs_away: 'Away Underdogs',
  favorites_home: 'Home Favorites',
  favorites_away: 'Away Favorites',
  all_home: 'Home Teams',
  all_away: 'Away Teams'
}
const validTrendMarkets = new Set(['spread', 'moneyline', 'total'])
const quickStatsCache = new Map()

function asString(value) {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

function asNumber(value) {
  if (value === null || value === undefined || value === '') return null
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

function pick(row, keys = []) {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== '') {
      return row[key]
    }
  }
  return null
}

function normalizeTimestamp(value) {
  if (!value) return ''
  const date = new Date(normalizeUtcTimestampInput(value))
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString()
}

function normalizeUtcTimestampInput(value) {
  if (value instanceof Date) return value
  const raw = String(value).trim()
  if (!raw) return raw
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(raw)) return raw
  if (/^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}/.test(raw)) return `${raw.replace(' ', 'T')}Z`
  return raw
}

function normalizeDate(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

function normalizeTimeframe(value) {
  const raw = asString(value)
  if (!raw) return 'Unspecified'

  const normalized = raw.toLowerCase()
  if (normalized.includes('current') && normalized.includes('season')) return 'Current Season'
  if (normalized.includes('full') && normalized.includes('season')) return 'Current Season'
  if (normalized === 'season') return 'Current Season'
  if (normalized === '1d' || normalized.includes('1 day')) return '1d'
  if (normalized === '7d' || normalized.includes('7 day') || normalized.includes('1 week')) return '7d'
  if (normalized === '1m' || normalized.includes('1 month')) return '1m'
  if (normalized === '3m' || normalized.includes('3 month')) return '3m'

  return raw
}

function timeframeRank(value) {
  const normalized = asString(value).toLowerCase()
  const idx = timeframePriority.findIndex((item) => normalized.includes(item))
  return idx === -1 ? 999 : idx
}

function normalizeMarketKey(value) {
  const raw = asString(value).toLowerCase()
  if (!raw) return ''
  if (
    raw.includes('spread') ||
    raw.includes('run line') ||
    raw.includes('runline') ||
    raw.includes('point spread') ||
    raw === 'ats' ||
    raw === 'spreads'
  ) {
    return 'spread'
  }
  if (
    raw.includes('moneyline') ||
    raw.includes('money line') ||
    raw === 'ml' ||
    raw === 'h2h' ||
    raw.includes('money_line')
  ) {
    return 'moneyline'
  }
  if (raw.includes('total') || raw.includes('over/under') || raw === 'ou' || raw === 'totals') return 'total'
  return raw
}

function normalizeTrendSide(row) {
  const raw = asString(pick(row, ['side', 'direction', 'selection', 'bet_side', 'market_side', 'total_side', 'outcome', 'pick', 'wager_side']))
  const normalized = raw.toLowerCase()
  if (!normalized) return ''
  if (normalized.includes('over') || normalized === 'o') return 'Over'
  if (normalized.includes('under') || normalized === 'u') return 'Under'
  if (normalized.includes('away')) return 'Away'
  if (normalized.includes('home')) return 'Home'
  if (normalized.includes('favorite') || normalized.includes('fav')) return 'Favorite'
  if (normalized.includes('underdog') || normalized.includes('dog')) return 'Underdog'
  return raw
}

function normalizeCategoryTrendSide(value) {
  const normalized = asString(value).toLowerCase()
  if (!normalized) return ''
  if (normalized.includes('over')) return 'Over'
  if (normalized.includes('under')) return 'Under'
  return ''
}

function normalizeRecord(row) {
  const wins = asNumber(pick(row, ['win_count', 'wins']))
  const losses = asNumber(pick(row, ['loss_count', 'losses']))
  const pushes = asNumber(pick(row, ['push_count', 'pushes']))
  const pickems = asNumber(pick(row, ['pickem_count', 'pickems']))
  const gamesPlayedValues = [wins, losses, pushes, pickems].filter((value) => Number.isFinite(value))

  const explicitRecord = asString(pick(row, ['record', 'win_loss_record']))

  let record = explicitRecord
  if (!record && wins !== null && losses !== null) {
    record = `${wins}-${losses}`
    if (pushes !== null && pushes > 0) record = `${record}-${pushes}`
  }

  let winRate = asNumber(pick(row, ['win_rate', 'winrate', 'pct', 'percentage']))
  if (winRate === null && wins !== null && losses !== null) {
    const decisionCount = wins + losses
    winRate = decisionCount > 0 ? (wins / decisionCount) * 100 : null
  }
  if (winRate !== null && winRate > 0 && winRate <= 1) {
    winRate *= 100
  }

  let roi = asNumber(pick(row, ['roi', 'roi_percent', 'return_on_investment', 'return_pct', 'profit_roi']))
  if (roi !== null && Math.abs(roi) > 0 && Math.abs(roi) <= 1) {
    roi *= 100
  }

  return {
    wins,
    losses,
    pushes,
    pickems,
    gamesPlayed: gamesPlayedValues.length ? gamesPlayedValues.reduce((sum, value) => sum + value, 0) : null,
    record,
    winRate,
    roi
  }
}

function normalizeLeagueTrendRow(row) {
  const stats = normalizeRecord(row)
  const rawCategory = asString(pick(row, ['category', 'segment', 'source'])) || 'General'
  const normalizedCategoryKey = rawCategory.toLowerCase()

  return {
    id: asString(pick(row, ['id'])) || JSON.stringify(row),
    league: asString(pick(row, ['league', 'sport', 'league_name', 'competition'])) || 'Unknown League',
    leagueLogo: asString(pick(row, ['league_logo'])),
    market: normalizeMarketKey(pick(row, ['market', 'bet_type', 'wager_type'])) || 'unspecified',
    side: normalizeTrendSide(row) || normalizeCategoryTrendSide(rawCategory),
    timeframe: normalizeTimeframe(pick(row, ['time_frame', 'timeframe', 'window', 'period'])),
    category: rawCategory,
    categoryLabel: categoryDisplayMap[normalizedCategoryKey] || rawCategory,
    createdFor: normalizeDate(pick(row, ['created_for', 'snapshot_date', 'date', 'event_date', 'day', 'as_of'])),
    updatedAt: normalizeTimestamp(pick(row, ['updated_at', 'created_at', 'timestamp', 'created_for'])),
    ...stats
  }
}

function normalizeTeamTrendRow(row) {
  const stats = normalizeRecord(row)
  const category = asString(pick(row, ['category', 'team_name', 'team']))
  const league = asString(normalizeLeague(row)) || 'Unknown League'
  const market = normalizeMarketKey(normalizeMarket(row)) || 'unspecified'
  const timeframe = normalizeTimeframe(pick(row, ['time_frame', 'timeframe', 'window', 'period']))
  const categoryKey = `${category}__${league}__${market}`

  return {
    id: asString(pick(row, ['id'])) || `${categoryKey}__${timeframe}`,
    categoryKey,
    entityName: category,
    teamName: asString(normalizeTeamName(row)),
    league,
    leagueLogo: asString(pick(row, ['league_logo'])),
    market,
    side: normalizeTrendSide(row) || normalizeCategoryTrendSide(category),
    timeframe,
    logoUrl: asString(pick(row, ['team_logo', 'logo_url', 'logo', 'image_url'])),
    updatedAt: normalizeTimestamp(pick(row, ['updated_at', 'created_at', 'timestamp'])),
    ...stats
  }
}

const sportsbookLabelMap = {
  betonline: 'BetOnline',
  betrivers: 'BetRivers',
  betus: 'BetUS',
  lowvig: 'LowVig',
  mybookie: 'MyBookie',
  dk: 'DraftKings',
  fd: 'FanDuel',
  fanatics: 'Fanatics',
  bovada: 'Bovada',
  mgm: 'BetMGM',
  caesars: 'Caesars'
}

const sportsbookSortOrder = ['dk', 'fd', 'mgm', 'caesars', 'bovada', 'fanatics', 'betrivers', 'betonline', 'betus', 'mybookie', 'lowvig']
const sportsbookCodeAliases = {
  draftkings: 'dk',
  fanduel: 'fd',
  betmgm: 'mgm',
  caesars: 'caesars',
  bovada: 'bovada',
  fanatics: 'fanatics',
  betrivers: 'betrivers',
  betonline: 'betonline',
  betonlineag: 'betonline',
  betus: 'betus',
  mybookie: 'mybookie',
  mybookieag: 'mybookie',
  lowvig: 'lowvig',
  lowvigag: 'lowvig'
}

function sortBooks(books = []) {
  return books.slice().sort((a, b) => {
    const aIdx = sportsbookSortOrder.indexOf(a.code)
    const bIdx = sportsbookSortOrder.indexOf(b.code)

    if (aIdx !== -1 || bIdx !== -1) {
      if (aIdx === -1) return 1
      if (bIdx === -1) return -1
      if (aIdx !== bIdx) return aIdx - bIdx
    }

    return a.label.localeCompare(b.label)
  })
}

function sportsbookCodeFromLabel(label) {
  const normalized = String(label || '').toLowerCase().replace(/[^a-z0-9]/g, '')
  return sportsbookCodeAliases[normalized] || normalized
}

function extractPairedBooks(row, { homePattern, awayPattern, homeField, awayField, valuePattern, valueField }) {
  const byBook = new Map()

  Object.keys(row).forEach((field) => {
    const valueMatch = valuePattern ? field.match(valuePattern) : null
    if (valueMatch) {
      const code = valueMatch[1].toLowerCase()
      if (!byBook.has(code)) {
        byBook.set(code, { code, label: sportsbookLabelMap[code] || code.toUpperCase(), [homeField]: null, [awayField]: null })
      }
      byBook.get(code)[valueField] = asNumber(row[field])
      return
    }

    const homeMatch = field.match(homePattern)
    if (homeMatch) {
      const code = homeMatch[1].toLowerCase()
      if (!byBook.has(code)) {
        byBook.set(code, { code, label: sportsbookLabelMap[code] || code.toUpperCase(), [homeField]: null, [awayField]: null })
      }
      byBook.get(code)[homeField] = asNumber(row[field])
      return
    }

    const awayMatch = field.match(awayPattern)
    if (awayMatch) {
      const code = awayMatch[1].toLowerCase()
      if (!byBook.has(code)) {
        byBook.set(code, { code, label: sportsbookLabelMap[code] || code.toUpperCase(), [homeField]: null, [awayField]: null })
      }
      byBook.get(code)[awayField] = asNumber(row[field])
    }
  })

  return sortBooks(Array.from(byBook.values()).filter((book) => book[homeField] !== null || book[awayField] !== null))
}

function extractMoneylineBooks(row) {
  return extractPairedBooks(row, {
    homePattern: /^ml_(.+)_home_price$/i,
    awayPattern: /^ml_(.+)_away_price$/i,
    homeField: 'homePrice',
    awayField: 'awayPrice'
  })
}

function extractSpreadBooks(row) {
  return extractPairedBooks(row, {
    homePattern: /^spread_(.+)_home_price$/i,
    awayPattern: /^spread_(.+)_away_price$/i,
    valuePattern: /^spread_(.+)_value$/i,
    homeField: 'homePrice',
    awayField: 'awayPrice',
    valueField: 'homeSpread'
  })
}

function extractTotalBooks(row) {
  return extractPairedBooks(row, {
    homePattern: /^total_(.+)_over_price$/i,
    awayPattern: /^total_(.+)_under_price$/i,
    homeField: 'overPrice',
    awayField: 'underPrice'
  })
}

function normalizePredictionVenue(row, prefix) {
  return {
    moneyline: {
      homePrice: asNumber(row[`${prefix}_ml_home_price`]),
      awayPrice: asNumber(row[`${prefix}_ml_away_price`])
    },
    spread: {
      homePrice: asNumber(row[`${prefix}_spread_home_price`]),
      awayPrice: asNumber(row[`${prefix}_spread_away_price`])
    },
    total: {
      overPrice: asNumber(row[`${prefix}_total_over_price`]),
      underPrice: asNumber(row[`${prefix}_total_under_price`])
    },
    volumeUsd: asNumber(row[`${prefix}_volume_usd`]),
    oiUsd: asNumber(row[`${prefix}_oi_usd`]),
    allVolumeUsd: asNumber(row[`${prefix}_all_volume_usd`]),
    allOiUsd: asNumber(row[`${prefix}_all_oi_usd`]),
    spreadVolumeUsd: asNumber(row[`${prefix}_spread_volume_usd`]),
    spreadOiUsd: asNumber(row[`${prefix}_spread_oi_usd`]),
    totalVolumeUsd: asNumber(row[`${prefix}_total_volume_usd`]),
    totalOiUsd: asNumber(row[`${prefix}_total_oi_usd`])
  }
}

function normalizeGameMarketSignals(row, prefix) {
  return {
    bookRisk: asNumber(row[`${prefix}_book_risk`]),
    disparity: asNumber(row[`${prefix}_disparity`]),
    pickPopularity: asNumber(row[`${prefix}_pick_popularity`]),
    platformSpread: asNumber(row[`${prefix}_platform_spread`]),
    volatility: asNumber(row[`${prefix}_volatility`])
  }
}

function normalizeDraftKingsSplits(row) {
  return {
    moneyline: {
      awayHandle: asNumber(row.ml_dk_away_handle),
      homeHandle: asNumber(row.ml_dk_home_handle),
      awayVolume: asNumber(row.ml_dk_away_volume),
      homeVolume: asNumber(row.ml_dk_home_volume)
    },
    spread: {
      awayHandle: asNumber(row.spread_dk_away_handle),
      homeHandle: asNumber(row.spread_dk_home_handle),
      awayVolume: asNumber(row.spread_dk_away_volume),
      homeVolume: asNumber(row.spread_dk_home_volume)
    },
    total: {
      overHandle: asNumber(row.total_dk_over_handle),
      underHandle: asNumber(row.total_dk_under_handle),
      overVolume: asNumber(row.total_dk_over_volume),
      underVolume: asNumber(row.total_dk_under_volume)
    }
  }
}

function normalizeDraftKingsHistory(row) {
  const history = typeof row.dk_splits_history === 'string'
    ? safeJsonParse(row.dk_splits_history)
    : row.dk_splits_history

  return {
    moneyline: normalizeDraftKingsMarketHistory(history?.moneyline, {
      firstHandle: 'away_handle',
      secondHandle: 'home_handle',
      firstVolume: 'away_volume',
      secondVolume: 'home_volume'
    }),
    spread: normalizeDraftKingsMarketHistory(history?.spread, {
      firstHandle: 'away_handle',
      secondHandle: 'home_handle',
      firstVolume: 'away_volume',
      secondVolume: 'home_volume'
    }),
    total: normalizeDraftKingsMarketHistory(history?.total, {
      firstHandle: 'over_handle',
      secondHandle: 'under_handle',
      firstVolume: 'over_volume',
      secondVolume: 'under_volume'
    })
  }
}

function normalizePredictionMarketHistory(row) {
  const history = typeof row.prediction_market_history === 'string'
    ? safeJsonParse(row.prediction_market_history)
    : row.prediction_market_history

  return {
    kalshi: normalizePredictionVenueHistory(history?.kalshi),
    polymarket: normalizePredictionVenueHistory(history?.polymarket)
  }
}

function normalizePredictionMarketPriceHistory(row) {
  const history = typeof row.prediction_market_history === 'string'
    ? safeJsonParse(row.prediction_market_history)
    : row.prediction_market_history

  return {
    kalshi: normalizePredictionVenuePriceHistory(history?.kalshi),
    polymarket: normalizePredictionVenuePriceHistory(history?.polymarket)
  }
}

function normalizePredictionVenuePriceHistory(history) {
  return {
    moneyline: normalizePredictionMarketSidePrices(history?.moneyline, ['away', 'home']),
    spread: normalizePredictionMarketSidePrices(history?.spread, ['away', 'home']),
    total: normalizePredictionMarketSidePrices(history?.total, ['over', 'under'])
  }
}

function normalizePredictionMarketSidePrices(marketHistory, sides = []) {
  return sides.reduce((acc, side) => {
    const values = Array.isArray(marketHistory?.[side]) ? marketHistory[side] : []
    acc[side] = values
      .map((point) => ({
        ts: normalizeTimestamp(point.ts),
        price: asNumber(point.price),
        line: asNumber(point.line),
        volumeUsd: asNumber(point.volume_usd),
        openInterestUsd: asNumber(point.open_interest_usd)
      }))
      .filter((point) => point.ts && point.price !== null)
      .sort((a, b) => a.ts.localeCompare(b.ts))
    return acc
  }, {})
}

function normalizePredictionVenueHistory(history) {
  return {
    moneyline: normalizePredictionMarketPoints(history?.moneyline, ['away', 'home']),
    spread: normalizePredictionMarketPoints(history?.spread, ['away', 'home']),
    total: normalizePredictionMarketPoints(history?.total, ['over', 'under'])
  }
}

function normalizePredictionMarketPoints(marketHistory, sides = []) {
  const primarySide = sides.find((side) => Array.isArray(marketHistory?.[side]))
  if (!primarySide) return []

  return marketHistory[primarySide]
    .map((point) => ({
      ts: normalizeTimestamp(point.ts),
      volumeUsd: asNumber(point.volume_usd),
      openInterestUsd: asNumber(point.open_interest_usd)
    }))
    .filter((point) => point.ts && (point.volumeUsd !== null || point.openInterestUsd !== null))
    .sort((a, b) => a.ts.localeCompare(b.ts))
}

function normalizeDraftKingsMarketHistory(values, keys) {
  if (!Array.isArray(values)) return []

  return values
    .map((point) => ({
      ts: normalizeTimestamp(point.ts),
      firstHandle: asNumber(point[keys.firstHandle]),
      secondHandle: asNumber(point[keys.secondHandle]),
      firstVolume: asNumber(point[keys.firstVolume]),
      secondVolume: asNumber(point[keys.secondVolume])
    }))
    .filter((point) => point.ts && (point.firstHandle !== null || point.secondHandle !== null || point.firstVolume !== null || point.secondVolume !== null))
    .sort((a, b) => a.ts.localeCompare(b.ts))
}

function normalizeOddsHistory(row) {
  const history = typeof row.odds_history === 'string'
    ? safeJsonParse(row.odds_history)
    : row.odds_history

  return {
    moneyline: normalizeSportsbookOddsMarketHistory(history?.moneyline, normalizeMoneylineOddsPoint),
    spread: normalizeSportsbookOddsMarketHistory(history?.spread, normalizeSpreadOddsPoint),
    total: normalizeSportsbookOddsMarketHistory(history?.total, normalizeTotalOddsPoint)
  }
}

function normalizeSportsbookOddsMarketHistory(marketHistory, normalizePoint) {
  if (!marketHistory || typeof marketHistory !== 'object') return []

  return sortBooks(Object.entries(marketHistory)
    .map(([label, points]) => {
      const code = sportsbookCodeFromLabel(label)
      const normalizedPoints = Array.isArray(points)
        ? points
            .map(normalizePoint)
            .filter((point) => point.ts)
            .sort((a, b) => a.ts.localeCompare(b.ts))
        : []

      return {
        code,
        label: sportsbookLabelMap[code] || label,
        points: normalizedPoints
      }
    })
    .filter((book) => book.points.length > 0))
}

function normalizeMoneylineOddsPoint(point = {}) {
  return {
    ts: normalizeTimestamp(point.ts),
    awayPrice: asNumber(point.away_price),
    homePrice: asNumber(point.home_price),
    awayImplied: asNumber(point.away_implied),
    homeImplied: asNumber(point.home_implied)
  }
}

function normalizeSpreadOddsPoint(point = {}) {
  return {
    ts: normalizeTimestamp(point.ts),
    awayLine: asNumber(point.away_line),
    homeLine: asNumber(point.home_line),
    awayPrice: asNumber(point.away_price),
    homePrice: asNumber(point.home_price),
    awayImplied: asNumber(point.away_implied),
    homeImplied: asNumber(point.home_implied)
  }
}

function normalizeTotalOddsPoint(point = {}) {
  return {
    ts: normalizeTimestamp(point.ts),
    line: asNumber(point.line),
    overPrice: asNumber(point.over_price),
    underPrice: asNumber(point.under_price),
    overImplied: asNumber(point.over_implied),
    underImplied: asNumber(point.under_implied)
  }
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

function normalizeGamePreviewRow(row) {
  const gameId = asString(pick(row, ['game_id', 'event_id', 'id']))
  const awayTeam = asString(pick(row, ['away_team', 'away_team_name', 'away'])) || 'Away Team'
  const homeTeam = asString(pick(row, ['home_team', 'home_team_name', 'home'])) || 'Home Team'
  const startTime = normalizeTimestamp(pick(row, ['game_start_time', 'game_time', 'start_time', 'scheduled_at', 'event_time']))

  return {
    id: gameId || JSON.stringify(row),
    gameId,
    gameSlug: gameId || `${awayTeam}-at-${homeTeam}-${startTime}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
    league: asString(pick(row, ['league_name', 'league', 'sport'])) || 'Unknown League',
    leagueLogo: asString(pick(row, ['league_logo'])),
    homeTeam,
    homeTeamLogo: asString(pick(row, ['home_team_logo'])),
    awayTeam,
    awayTeamLogo: asString(pick(row, ['away_team_logo'])),
    startTime,
    spread: asNumber(pick(row, ['spread_value', 'spread', 'spread_line'])),
    total: asNumber(pick(row, ['total_value', 'total', 'total_line', 'over_under'])),
    moneylineBooks: extractMoneylineBooks(row),
    spreadBooks: extractSpreadBooks(row),
    totalBooks: extractTotalBooks(row),
    kalshi: normalizePredictionVenue(row, 'kalshi'),
    polymarket: normalizePredictionVenue(row, 'poly'),
    marketSignals: {
      moneyline: normalizeGameMarketSignals(row, 'ml'),
      spread: normalizeGameMarketSignals(row, 'spread'),
      total: normalizeGameMarketSignals(row, 'total')
    },
    draftKingsSplits: normalizeDraftKingsSplits(row),
    draftKingsHistory: normalizeDraftKingsHistory(row),
    oddsHistory: normalizeOddsHistory(row),
    predictionMarketHistory: normalizePredictionMarketHistory(row),
    predictionMarketPriceHistory: normalizePredictionMarketPriceHistory(row),
    volumeRatio: asNumber(row.volume_ratio),
    allVolumeRatio: asNumber(row.all_volume_ratio),
    updatedAt: normalizeTimestamp(pick(row, ['updated_at', 'created_at', 'timestamp']))
  }
}

function emptyResult(message) {
  return { rows: [], errorMessage: message }
}

function getCachedQuickStatsResult(cacheKey, loadResult) {
  if (!quickStatsCache.has(cacheKey)) {
    quickStatsCache.set(
      cacheKey,
      Promise.resolve()
        .then(loadResult)
        .catch((error) => {
          quickStatsCache.delete(cacheKey)
          throw error
        })
    )
  }

  return quickStatsCache.get(cacheKey)
}

function applyOrdering(query, orderBy = []) {
  return orderBy.reduce((currentQuery, order) => {
    return currentQuery.order(order.column, {
      ascending: order.ascending ?? true,
      nullsFirst: order.nullsFirst
    })
  }, query)
}

function applyFilters(query, filters = []) {
  return filters.reduce((currentQuery, filter) => {
    if (!filter || !filter.column) return currentQuery
    if (filter.operator === 'eq') return currentQuery.eq(filter.column, filter.value)
    if (filter.operator === 'in') return currentQuery.in(filter.column, filter.value)
    return currentQuery
  }, query)
}

async function fetchRows(tableName, options = {}) {
  const status = getPublicSupabaseStatus()
  if (!status.available || !publicSupabase) {
    return emptyResult(status.message)
  }

  const pageSize = 1000
  const rows = []
  let pageStart = 0
  const orderBy = Array.isArray(options.orderBy) ? options.orderBy : []
  const filters = Array.isArray(options.filters) ? options.filters : []
  const selectClause = options.select || '*'

  while (true) {
    const pageEnd = pageStart + pageSize - 1
    let query = publicSupabase
      .from(tableName)
      .select(selectClause)
      .range(pageStart, pageEnd)

    query = applyFilters(query, filters)

    if (orderBy.length > 0) {
      query = applyOrdering(query, orderBy)
    }

    let { data, error } = await query

    // If an optimized query drifts from the live table schema, fall back to a
    // broader version instead of failing the whole public page.
    if (error) {
      if (orderBy.length > 0) {
        let fallbackQuery = publicSupabase
          .from(tableName)
          .select(selectClause)
          .range(pageStart, pageEnd)

        fallbackQuery = applyFilters(fallbackQuery, filters)

        const fallbackResult = await fallbackQuery
        data = fallbackResult.data
        error = fallbackResult.error
      }

      if (error && selectClause !== '*') {
        let fallbackQuery = publicSupabase
          .from(tableName)
          .select('*')
          .range(pageStart, pageEnd)

        fallbackQuery = applyFilters(fallbackQuery, filters)

        if (orderBy.length > 0) {
          fallbackQuery = applyOrdering(fallbackQuery, orderBy)
        }

        const fallbackResult = await fallbackQuery
        data = fallbackResult.data
        error = fallbackResult.error
      }

      if (error) {
        return emptyResult(`Public data request failed for ${tableName}.`)
      }
    }

    const pageRows = data || []
    rows.push(...pageRows)

    if (pageRows.length < pageSize) {
      break
    }

    pageStart += pageSize
  }

  return { rows, errorMessage: '' }
}

function createTeamLogoMapFromGameRows(gameRows = []) {
  const byTeam = new Map()

  gameRows.forEach((row) => {
    const league = asString(pick(row, ['league_name', 'league', 'sport']))
    const awayTeam = asString(pick(row, ['away_team', 'away_team_name', 'away']))
    const homeTeam = asString(pick(row, ['home_team', 'home_team_name', 'home']))
    const awayLogo = asString(pick(row, ['away_team_logo']))
    const homeLogo = asString(pick(row, ['home_team_logo']))

    if (league && awayTeam && awayLogo) {
      byTeam.set(`${awayTeam}__${league}`.toLowerCase(), awayLogo)
    }
    if (league && homeTeam && homeLogo) {
      byTeam.set(`${homeTeam}__${league}`.toLowerCase(), homeLogo)
    }
  })

  return byTeam
}

function createTeamTrendCatalogEntry(row) {
  const entityName = asString(normalizeTeamName(row))
  const league = asString(normalizeLeague(row)) || 'Unknown League'
  const market = normalizeMarketKey(normalizeMarket(row)) || 'unspecified'
  const timeframe = normalizeTimeframe(pick(row, ['time_frame', 'timeframe', 'window', 'period']))
  const rawLeague = asString(pick(row, ['league']))
  const rawMarket = asString(pick(row, ['market']))
  const rawTimeframe = asString(pick(row, ['time_frame']))

  return {
    entityName,
    league,
    market,
    timeframe,
    rawLeague,
    rawMarket,
    rawTimeframe
  }
}

function filterTeamTrendRowsBySelection(rows, { league, market, timeframe }) {
  return rows.filter((row) => {
    if (row.league !== league) return false
    if (row.market !== market) return false
    if (row.timeframe !== timeframe) return false
    return true
  })
}

function getLeagueFilterValues(league) {
  const normalized = asString(league).toUpperCase()

  switch (normalized) {
    case 'NFL':
      return ['NFL', 'American Football NFL']
    case 'MLB':
      return ['MLB', 'Baseball MLB']
    case 'NBA':
      return ['NBA', 'Basketball NBA']
    case 'NHL':
      return ['NHL', 'Ice Hockey NHL']
    case 'NCAAF':
      return ['NCAAF', 'NCAA Football', 'American Football NCAAF']
    case 'NCAAB':
      return ['NCAAB', 'NCAAM', 'NCAA Basketball', 'Basketball NCAAB', 'Basketball NCAAM']
    default:
      return normalized ? [league] : []
  }
}

function getMarketFilterValues(market) {
  const normalized = normalizeMarketKey(market)

  switch (normalized) {
    case 'spread':
      return ['spread', 'spreads', 'Spread', 'Spreads', 'ats', 'ATS', 'point spread', 'Point Spread']
    case 'moneyline':
      return ['moneyline', 'Moneyline', 'money line', 'Money Line', 'ml', 'ML', 'h2h', 'H2H', 'money_line']
    case 'total':
      return ['total', 'totals', 'Total', 'Totals', 'over/under', 'Over/Under', 'ou', 'OU']
    default:
      return normalized ? [market] : []
  }
}

function getTimeframeFilterValues(timeframe) {
  const normalized = normalizeTimeframe(timeframe)

  switch (normalized) {
    case 'Current Season':
      return ['Current Season', 'current season', 'full-season', 'full season', 'Full Season', 'season', 'Season']
    case '3m':
      return ['3m', '3M', '3 month', '3 months']
    case '1m':
      return ['1m', '1M', '1 month']
    case '7d':
      return ['7d', '7D', '7 day', '7 days', '1 week']
    case '1d':
      return ['1d', '1D', '1 day']
    default:
      return normalized ? [timeframe] : []
  }
}

async function getTeamTrendCatalog() {
  return getCachedQuickStatsResult('teamTrendCatalog', async () => {
    const { rows, errorMessage } = await fetchRows('team_trends', {
      select: 'category,team_name,team,league,sport,league_name,competition,market,bet_type,wager_type,time_frame,timeframe,window,period',
      orderBy: [
        { column: 'league', ascending: true },
        { column: 'market', ascending: true },
        { column: 'time_frame', ascending: true },
        { column: 'category', ascending: true }
      ]
    })

    const catalogRows = rows
      .map(createTeamTrendCatalogEntry)
      .filter((row) => row.entityName)
      .filter((row) => validTrendMarkets.has(row.market))

    const rawFilterMap = new Map()

    catalogRows.forEach((row) => {
      const key = `${row.league}|${row.market}|${row.timeframe}`
      if (!rawFilterMap.has(key)) {
        rawFilterMap.set(key, {
          leagues: new Set(),
          markets: new Set(),
          timeframes: new Set()
        })
      }

      const entry = rawFilterMap.get(key)
      if (row.rawLeague) entry.leagues.add(row.rawLeague)
      if (row.rawMarket) entry.markets.add(row.rawMarket)
      if (row.rawTimeframe) entry.timeframes.add(row.rawTimeframe)
    })

    const slugMap = createTrendEntitySlugMap(catalogRows)

    return {
      rows: catalogRows,
      slugMap,
      rawFilterMap,
      errorMessage
    }
  })
}

export async function getLeagueTrends() {
  return getCachedQuickStatsResult('leagueTrends', async () => {
    const { rows, errorMessage } = await fetchRows('league_trends', {
      orderBy: [
        { column: 'league', ascending: true },
        { column: 'market', ascending: true },
        { column: 'time_frame', ascending: true },
        { column: 'created_for', ascending: false }
      ]
    })

    const filtered = rows
      .map(normalizeLeagueTrendRow)
      .filter((row) => !/draftkings|circa/i.test(row.category))
      .sort((a, b) => {
        if (a.league !== b.league) return a.league.localeCompare(b.league)
        if (a.market !== b.market) return a.market.localeCompare(b.market)
        if (a.timeframe !== b.timeframe) return timeframeRank(a.timeframe) - timeframeRank(b.timeframe)
        return b.createdFor.localeCompare(a.createdFor)
      })

    return { rows: filtered, errorMessage }
  })
}

export async function getTeamTrends() {
  return getCachedQuickStatsResult('teamTrends', async () => {
    const [teamTrendResult, gamePreviewResult] = await Promise.all([
      fetchRows('team_trends', {
        orderBy: [
          { column: 'league', ascending: true },
          { column: 'market', ascending: true },
          { column: 'category', ascending: true },
          { column: 'time_frame', ascending: true }
        ]
      }),
      getCachedQuickStatsResult('teamTrendLogoRows', async () => {
        return fetchRows('game_previews', {
          orderBy: [{ column: 'updated_at', ascending: false }]
        })
      })
    ])

    const teamLogoMap = createTeamLogoMapFromGameRows(gamePreviewResult.rows)

    const normalized = teamTrendResult.rows
      .map(normalizeTeamTrendRow)
      .filter((row) => row.entityName)
      .filter((row) => validTrendMarkets.has(row.market))
      .map((row) => {
        if (row.logoUrl) return row
        const teamLogoKey = `${row.entityName}__${row.league}`.toLowerCase()
        return {
          ...row,
          logoUrl: teamLogoMap.get(teamLogoKey) || ''
        }
      })
      .sort((a, b) => {
        if (a.league !== b.league) return a.league.localeCompare(b.league)
        if (a.market !== b.market) return a.market.localeCompare(b.market)
        if (a.entityName !== b.entityName) return a.entityName.localeCompare(b.entityName)
        return timeframeRank(a.timeframe) - timeframeRank(b.timeframe)
      })

    const slugMap = createTrendEntitySlugMap(normalized)

    return {
      rows: normalized.map((row) => {
        const teamKey = buildTrendEntityKey({
          category: row.entityName,
          league: row.league,
          market: row.market
        })
        return {
          ...row,
          teamSlug: slugMap.entityToSlug.get(teamKey) || ''
        }
      }),
      slugToTeam: slugMap.slugToEntity,
      errorMessage: teamTrendResult.errorMessage || gamePreviewResult.errorMessage
    }
  })
}

export async function getTeamRankingRows() {
  return getCachedQuickStatsResult('teamRankingRows', async () => {
    const { rows, errorMessage } = await fetchRows('team_trends', {
      select: [
        'id',
        'category',
        'team_name',
        'team',
        'league',
        'sport',
        'league_name',
        'competition',
        'league_logo',
        'market',
        'bet_type',
        'wager_type',
        'time_frame',
        'timeframe',
        'window',
        'period',
        'team_logo',
        'logo_url',
        'logo',
        'image_url',
        'updated_at',
        'created_at',
        'timestamp',
        'win_count',
        'wins',
        'loss_count',
        'losses',
        'push_count',
        'pushes',
        'pickem_count',
        'pickems',
        'record',
        'win_loss_record',
        'win_rate',
        'winrate',
        'pct',
        'percentage'
      ].join(','),
      orderBy: [
        { column: 'league', ascending: true },
        { column: 'market', ascending: true },
        { column: 'category', ascending: true },
        { column: 'time_frame', ascending: true }
      ]
    })

    const normalized = rows
      .map(normalizeTeamTrendRow)
      .filter((row) => row.entityName)
      .filter((row) => validTrendMarkets.has(row.market))
      .sort((a, b) => {
        if (a.league !== b.league) return a.league.localeCompare(b.league)
        if (a.market !== b.market) return a.market.localeCompare(b.market)
        if (a.entityName !== b.entityName) return a.entityName.localeCompare(b.entityName)
        return timeframeRank(a.timeframe) - timeframeRank(b.timeframe)
      })

    const slugMap = createTrendEntitySlugMap(normalized)

    return {
      rows: normalized.map((row) => {
        const teamKey = buildTrendEntityKey({
          category: row.entityName,
          league: row.league,
          market: row.market
        })
        return {
          ...row,
          teamSlug: slugMap.entityToSlug.get(teamKey) || ''
        }
      }),
      errorMessage
    }
  })
}

export async function getTeamRankingSlice({ league, market, timeframe }) {
  const cacheKey = `teamRankingSlice:${league}|${market}|${timeframe}`

  return getCachedQuickStatsResult(cacheKey, async () => {
    const { rows, errorMessage } = await fetchRows('team_trends', {
      select: [
        'id',
        'category',
        'team_name',
        'team',
        'league',
        'sport',
        'league_name',
        'competition',
        'league_logo',
        'market',
        'bet_type',
        'wager_type',
        'time_frame',
        'timeframe',
        'window',
        'period',
        'team_logo',
        'logo_url',
        'logo',
        'image_url',
        'updated_at',
        'created_at',
        'timestamp',
        'win_count',
        'wins',
        'loss_count',
        'losses',
        'push_count',
        'pushes',
        'pickem_count',
        'pickems',
        'record',
        'win_loss_record',
        'win_rate',
        'winrate',
        'pct',
        'percentage'
      ].join(','),
      filters: [
        { column: 'league', operator: 'in', value: getLeagueFilterValues(league) },
        { column: 'market', operator: 'in', value: getMarketFilterValues(market) },
        { column: 'time_frame', operator: 'in', value: getTimeframeFilterValues(timeframe) }
      ],
      orderBy: [
        { column: 'win_count', ascending: false, nullsFirst: false },
        { column: 'category', ascending: true }
      ]
    })

    const normalized = rows
      .map(normalizeTeamTrendRow)
      .filter((row) => row.entityName)
      .filter((row) => validTrendMarkets.has(row.market))
      .filter((row) => row.league === league && row.market === market && row.timeframe === timeframe)

    const slugMap = createTrendEntitySlugMap(normalized)

    return {
      rows: normalized.map((row) => {
        const teamKey = buildTrendEntityKey({
          category: row.entityName,
          league: row.league,
          market: row.market
        })
        return {
          ...row,
          teamSlug: slugMap.entityToSlug.get(teamKey) || ''
        }
      }),
      errorMessage
    }
  })
}

export async function getTeamRankingFilterOptions() {
  const catalog = await getTeamTrendCatalog()

  return {
    rows: catalog.rows,
    slugToTeam: catalog.slugMap.slugToEntity,
    entityToSlug: catalog.slugMap.entityToSlug,
    errorMessage: catalog.errorMessage
  }
}

export async function getTeamRankingsForFilters({ league, market, timeframe }) {
  const cacheKey = `teamRankings:${league}|${market}|${timeframe}`
  const catalog = await getTeamTrendCatalog()
  const rawFilters = catalog.rawFilterMap.get(cacheKey)

  return getCachedQuickStatsResult(cacheKey, async () => {
    if (
      !rawFilters ||
      (!rawFilters.leagues.size && !rawFilters.markets.size && !rawFilters.timeframes.size)
    ) {
      const fallback = await getTeamTrends()
      return {
        rows: filterTeamTrendRowsBySelection(fallback.rows, { league, market, timeframe }),
        errorMessage: fallback.errorMessage || catalog.errorMessage
      }
    }

    const filters = []

    if (rawFilters?.leagues?.size) {
      filters.push({
        column: 'league',
        operator: 'in',
        value: Array.from(rawFilters.leagues)
      })
    }

    if (rawFilters?.markets?.size) {
      filters.push({
        column: 'market',
        operator: 'in',
        value: Array.from(rawFilters.markets)
      })
    }

    if (rawFilters?.timeframes?.size) {
      filters.push({
        column: 'time_frame',
        operator: 'in',
        value: Array.from(rawFilters.timeframes)
      })
    }

    const { rows, errorMessage } = await fetchRows('team_trends', {
      filters,
      orderBy: [
        { column: 'category', ascending: true },
        { column: 'updated_at', ascending: false }
      ]
    })

    const normalized = rows
      .map(normalizeTeamTrendRow)
      .filter((row) => row.entityName)
      .filter((row) => validTrendMarkets.has(row.market))
      .map((row) => {
        const teamKey = buildTrendEntityKey({
          category: row.entityName,
          league: row.league,
          market: row.market
        })

        return {
          ...row,
          teamSlug: catalog.slugMap.entityToSlug.get(teamKey) || ''
        }
      })

    if (normalized.length === 0 && !errorMessage) {
      const fallback = await getTeamTrends()
      return {
        rows: filterTeamTrendRowsBySelection(fallback.rows, { league, market, timeframe }),
        errorMessage: fallback.errorMessage || catalog.errorMessage
      }
    }

    return {
      rows: normalized,
      errorMessage: errorMessage || catalog.errorMessage
    }
  })
}

export async function getGamePreviews() {
  return getCachedQuickStatsResult('gamePreviews', async () => {
    const { rows, errorMessage } = await fetchRows('game_previews', {
      orderBy: [{ column: 'game_start_time', ascending: true }]
    })
    const normalized = rows
      .map(normalizeGamePreviewRow)
      .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''))

    return { rows: normalized, errorMessage }
  })
}
